import { useCallback, useState } from "react";
import { useCanManageDesignAccess } from "src/hooks/userAccess";

import { useDesignOptionId } from "./overlay/useDesignOptionId";
import {
  useDesignWorkspaceOptions,
  type DesignWorkspaceDesign,
} from "./useDesignWorkspaceOptions";
import useDesignsRepository from "./useDesignsRepository";

const FROZEN_WORKFLOW_STAGES = new Set(["approved", "implemented", "closed"]);

type DesignOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

type UseDesignOptionActionsOverride = {
  design: DesignWorkspaceDesign | null | undefined;
  refetch: () => Promise<unknown>;
};

/**
 * By default resolves the design (and its options) via the workspace
 * context. When `override` is supplied, the actions instead operate on an
 * already-fetched `design` and use the caller's own `refetch` — for
 * callers (e.g. the design detail page) that already loaded the design by
 * id and don't need a second workspace-scoped fetch.
 */
const useDesignOptionActions = (override?: UseDesignOptionActionsOverride) => {
  const { optionId, setOptionId } = useDesignOptionId();
  const { active, design, options, optionsLoading, refetch } =
    useDesignWorkspaceOptions(override);
  const designsRepository = useDesignsRepository();
  const canEditOptions = useCanManageDesignAccess();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const designId = design?.id;
  const baselineDataDate = design?.baselineDataDate ?? null;
  const frozen = FROZEN_WORKFLOW_STAGES.has(design?.workflowStage ?? "");

  const createOption = useCallback(
    async (name: string) => {
      if (!designId || frozen || !canEditOptions) return;
      setCreating(true);
      try {
        const created = await designsRepository.createDesignOption({
          id: designId,
          name,
        });
        await refetch();
        if (created?.id) {
          setOptionId(created.id);
        }
      } finally {
        setCreating(false);
      }
    },
    [canEditOptions, designId, designsRepository, frozen, refetch, setOptionId]
  );

  const renameOption = useCallback(
    async (option: DesignOption, name: string) => {
      if (!designId || frozen || !canEditOptions || option.isTrunk) return;
      setRenaming(true);
      try {
        await designsRepository.renameDesignOption({
          id: designId,
          optionId: option.id,
          name,
        });
        await refetch();
      } finally {
        setRenaming(false);
      }
    },
    [canEditOptions, designId, designsRepository, frozen, refetch]
  );

  const deleteOption = useCallback(
    async (option: DesignOption) => {
      if (!designId || frozen || !canEditOptions || option.isTrunk) return;
      const deleted = await designsRepository.deleteDesignOption({
        id: designId,
        optionId: option.id,
      });
      if (!deleted) return;
      await refetch();
    },
    [canEditOptions, designId, designsRepository, frozen, refetch]
  );

  return {
    // Show once the sandbox design resolves so "New option…" can seed trunk.
    visible: active && Boolean(design),
    optionId,
    setOptionId,
    options,
    optionsLoading,
    baselineDataDate,
    frozen: frozen || !canEditOptions,
    creating,
    renaming,
    createOption,
    renameOption,
    deleteOption,
  };
};

export default useDesignOptionActions;
